<template>
  <BaseModal
    :is-open="isOpen"
    title="Joining Game"
    :closable="false"
    max-width="450px"
  >
    <div class="join-status-container">
      <!-- Room Code Display -->
      <div class="room-code-display">
        <span class="room-label">Room</span>
        <span class="room-code">{{ roomCode }}</span>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps">
        <div
          v-for="(stepInfo, index) in steps"
          :key="index"
          class="step"
          :class="{
            'step-completed': index < currentStep,
            'step-active': index === currentStep,
            'step-pending': index > currentStep,
            'step-error': hasError && index === currentStep
          }"
        >
          <div class="step-icon">
            <span v-if="index < currentStep" class="material-symbols-outlined check-icon">check_circle</span>
            <span v-else-if="hasError && index === currentStep" class="material-symbols-outlined error-icon">error</span>
            <span v-else class="material-symbols-outlined" :class="{ 'spinning': index === currentStep }">
              {{ stepInfo.icon }}
            </span>
          </div>
          <div class="step-content">
            <span class="step-label">{{ stepInfo.label }}</span>
            <span v-if="index === currentStep && !hasError" class="step-status">{{ stepInfo.activeText }}</span>
          </div>
        </div>
      </div>

      <!-- Status Message -->
      <div class="status-message" :class="{ 'error': hasError }">
        <span class="material-symbols-outlined status-icon">{{ hasError ? 'warning' : 'info' }}</span>
        <span>{{ statusMessage }}</span>
      </div>

      <!-- Cancel Button -->
      <button @click="handleCancel" class="btn-cancel" :disabled="currentStep >= 2 && !hasError">
        <span class="material-symbols-outlined">close</span>
        <span>{{ hasError ? 'Close' : 'Cancel' }}</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * JoinStatusModal Component
 *
 * Shows progress during the join game flow with 3 steps:
 * 1. Sending Request - Initial request sent to server
 * 2. Waiting for Approval - Waiting for room creator to approve
 * 3. Joining Game - Seat assigned, transitioning to game
 *
 * Props:
 * - isOpen: Whether modal is visible
 * - currentStep: Current step (0, 1, or 2)
 * - roomCode: The room code being joined
 * - statusMessage: Additional status text
 * - hasError: Whether an error occurred
 *
 * Emits:
 * - cancel: User clicked cancel
 */

interface Props {
  isOpen: boolean
  currentStep: number
  roomCode: string
  statusMessage?: string
  hasError?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  currentStep: 0,
  roomCode: '',
  statusMessage: '',
  hasError: false,
})

const emit = defineEmits<{
  cancel: []
}>()

/**
 * Step definitions
 */
const steps = computed(() => [
  {
    label: 'Sending Request',
    icon: 'send',
    activeText: 'Connecting to server...',
  },
  {
    label: 'Waiting for Approval',
    icon: 'hourglass_top',
    activeText: 'Waiting for room creator...',
  },
  {
    label: 'Joining Game',
    icon: 'login',
    activeText: 'Setting up your seat...',
  },
])

/**
 * Default status message based on step
 */
const statusMessage = computed(() => {
  if (props.statusMessage) return props.statusMessage
  if (props.hasError) return 'Something went wrong. Please try again.'

  switch (props.currentStep) {
    case 0:
      return 'Sending your join request to the game server...'
    case 1:
      return 'The room creator will assign you to a seat.'
    case 2:
      return 'Almost there! Preparing your game view...'
    default:
      return ''
  }
})

/**
 * Handle cancel button
 */
function handleCancel(): void {
  emit('cancel')
}
</script>

<style scoped>
.join-status-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Room Code Display */
.room-code-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.room-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.5);
}

.room-code {
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 4px;
  color: #e8c547;
  font-family: 'Monaco', 'Consolas', monospace;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 320px;
}

.step {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
}

.step-completed {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
}

.step-active {
  background: rgba(232, 197, 71, 0.1);
  border-color: rgba(232, 197, 71, 0.4);
  box-shadow: 0 0 20px rgba(232, 197, 71, 0.15);
}

.step-pending {
  opacity: 0.5;
}

.step-error {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
}

/* Step Icon */
.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
}

.step-completed .step-icon {
  background: rgba(16, 185, 129, 0.2);
}

.step-active .step-icon {
  background: rgba(232, 197, 71, 0.2);
}

.step-error .step-icon {
  background: rgba(239, 68, 68, 0.2);
}

.step-icon .material-symbols-outlined {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.7);
}

.step-completed .step-icon .material-symbols-outlined,
.step-icon .check-icon {
  color: #34d399;
}

.step-active .step-icon .material-symbols-outlined {
  color: #e8c547;
}

.step-error .step-icon .material-symbols-outlined,
.step-icon .error-icon {
  color: #f87171;
}

/* Spinning animation for active step */
.spinning {
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Step Content */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
}

.step-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.step-completed .step-label {
  color: #34d399;
}

.step-active .step-label {
  color: #e8c547;
}

.step-error .step-label {
  color: #f87171;
}

.step-status {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Status Message */
.status-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  width: 100%;
  max-width: 320px;
}

.status-message.error {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

.status-icon {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.5);
}

.status-message.error .status-icon {
  color: #f87171;
}

.status-message span:last-child {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
}

.status-message.error span:last-child {
  color: #fca5a5;
}

/* Cancel Button */
.btn-cancel {
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  margin-top: 0.5rem;
}

.btn-cancel:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.4);
  color: #f87171;
}

.btn-cancel:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-cancel .material-symbols-outlined {
  font-size: 1rem;
}

/* Responsive */
@media (max-width: 480px) {
  .progress-steps,
  .status-message {
    max-width: 100%;
  }

  .step {
    padding: 0.875rem 1rem;
  }
}
</style>
